/*
 *   File : listing.js
 *   Author : https://evoqins.com
 *   Description : Super Admin Manage Stores listing page
 *   Integrations : react-toastify, bootstrap
 *   Version : 1.0.0
*/

import React, { useEffect, useRef, useState } from 'react';
import { Modal } from 'bootstrap';
import { toast } from 'react-toastify';

// Custom component
import { DataTableComponent } from '../../../Component/DataTable';
import { CustomSearchInput, CustomSelectBox } from '../../../Component/FormElements';
import { APIService } from '../../../Service';
import { EmptyScreen } from '../../../Component/EmptyComponent';
import { Loader } from '../../../Component/Others';
import { DownloadButton, PrimaryButton, TextButton } from '../../../Component/Buttons';
import { ManageProductModal, ManageStoreModal } from '../../../Component/Modal';
import { Icon } from '../../../Component/Icon';

const ITEMS_PER_PAGE = 20;

const PAGE_SORT_FIELDS = [
    'id',
    'name',
    'data_id',
    'location',
    'store_id',
    'mobile',
    'country_id',
    'country_name',
    'city_id',
    'is_enabled',
    'location_url',
    'name_arabic',
];

const StoreListing = () => {

    const isSortingRef = useRef(false);
    const skipListFetchRef = useRef(false);

    const [searchValue, setSearchValue] = useState("");
    const [storeData, setStoreData] = useState({});
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);

    const [toggleFilter, setToggleFilter] = useState(false);
    const [downloadLoader, setDownloadLoader] = useState(false);

    const [countryOptions, setCountryOptions] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [filterLoader, setFilterLoader] = useState(true);
    const [clearFilter, setClearFilter] = useState(false);

    const [uploadModal, setUploadModal] = useState(false);
    const [uploadResponse, setUploadResponse] = useState(false);
    const [modalBtnLoader, setModalBtnLoader] = useState(false);

    const [editStore, setEditStore] = useState(null);

    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);

    useEffect(() => {
        window.scrollTo({
            top: 0, behavior: 'smooth'
        });

        _getFilterData();
    }, []);

    useEffect(() => {
        if (skipListFetchRef.current) {
            skipListFetchRef.current = false;
            return;
        }
        _getStoreList();
    }, [currentPage, clearFilter]);

    useEffect(() => {
        if (uploadModal === true) {
            const upload_modal = new Modal(document.getElementById('upload-product-modal'));
            upload_modal.show();
        }
    }, [uploadModal]);

    useEffect(() => {
        if (editStore !== null) {
            const manage_store_modal = new Modal(document.getElementById('manage-store-modal'));
            manage_store_modal.show();
        }
    }, [editStore]);

    // handle paginate store list
    const _handlePageChange = (page_num) => {
        if (isSortingRef.current) {
            isSortingRef.current = false;
            return;
        }

        if (page_num === currentPage) {
            return;
        }

        setCurrentPage(page_num);

        window.scrollTo({
            top: 0, behavior: 'smooth'
        });
    };

    const _handleSort = (column, direction) => {
        const sortBy = column.sortField || column.id || null;

        if (!sortBy || !PAGE_SORT_FIELDS.includes(sortBy)) {
            return;
        }

        isSortingRef.current = true;
        setSortField(sortBy);
        setSortDirection(direction);
        setLoading(true);

        // Only skip the list useEffect when page will actually change.
        if (currentPage !== 1) {
            skipListFetchRef.current = true;
        }
        setCurrentPage(1);
        _getStoreList(1, searchValue, sortBy, direction);

        setTimeout(() => {
            isSortingRef.current = false;
        }, 0);
    };

    const _handleClearAll = () => {
        setClearFilter(true);
        setLoading(true);
        setCurrentPage(1);
        setSelectedCountry(countryOptions[0]);
        setSortField(null);
        setSortDirection(null);
    };

    // store list table
    const TABLE_COLUMNS = [
        {
            name: 'Store name',
            id: 'name',
            sortField: 'name',
            selector: row => row.name ? row.name : '-',
            cell: row => <>{row.name || '-'}</>,
            sortable: true,
            width: "280px"
        },
        {
            name: 'Store code',
            id: 'store_id',
            sortField: 'store_id',
            selector: row => row.store_id ? row.store_id : '-',
            width: "140px",
            sortable: true,
        },
        {
            name: 'Country',
            id: 'country_name',
            sortField: 'country_name',
            selector: row => row.country_name ? row.country_name : '-',
            width: "120px",
            sortable: true,
        },
        {
            name: 'City',
            id: 'location',
            sortField: 'location',
            selector: row => row.location ? row.location : '-',
            width: "140px",
            sortable: true,
        },
        {
            name: 'Phone',
            id: 'mobile',
            sortField: 'mobile',
            selector: row => row.mobile ? row.mobile : '-',
            width: "160px",
            sortable: true,
        },
        {
            name: 'Location URL',
            id: 'location_url',
            sortField: 'location_url',
            cell: row => (
                row.location_url ?
                    <a href={row.location_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="e-text-indigo e-alt-font-noto-sans e-font-14 e-font-weight-400 line-height-20px text-decoration-none text-decoration-hover-underline"
                        onClick={(e) => e.stopPropagation()}>
                        View location
                    </a>
                    :
                    <span className="e-text-davys-grey">-</span>
            ),
            width: "140px",
            sortable: true,
        },
        {
            name: 'Action',
            cell: row => (
                <span className="e-text-indigo e-alt-font-noto-sans e-font-14 e-font-weight-500 line-height-20px cursor-pointer text-decoration-hover-underline"
                    onClick={(e) => {
                        e.stopPropagation();
                        setEditStore(row);
                    }}>
                    Edit
                </span>
            ),
            width: "100px"
        },
    ];

    // API - Get filter data
    const _getFilterData = () => {
        setFilterLoader(true);

        let is_post = false;
        let url = "store/filter-data";
        let data = {};

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                const countries = response.data || [];
                const updated_data = countries.map((item) => ({
                    value: item.country_id,
                    label: item.country_name
                }));

                const updated_filter_data = [{ value: null, label: "All" }, ...updated_data];

                setCountryOptions(updated_filter_data);
                setSelectedCountry(updated_filter_data[0]);
            } else {
                setCountryOptions([{ value: null, label: "All" }]);
                setSelectedCountry({ value: null, label: "All" });
            }
            setFilterLoader(false);
        });
    };

    // API - Get all stores
    const _getStoreList = (
        page_num = currentPage,
        query = searchValue,
        sort_by = sortField,
        sort_order = sortDirection,
        country_id = selectedCountry?.value || null
    ) => {
        const effective_sort_by = sort_by && PAGE_SORT_FIELDS.includes(sort_by) ? sort_by : null;
        const effective_sort_order = effective_sort_by ? sort_order : null;

        setLoading(true);

        let is_post = true;
        let url = 'store/list';
        let data = {
            "page_num": page_num,
            "limit": ITEMS_PER_PAGE,
            "search_query": query,
            "country_id": country_id,
            "sort_by": effective_sort_by ? [effective_sort_by] : null,
            "order": effective_sort_order ? [effective_sort_order] : null,
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setStoreData(response.data);
            } else {
                setStoreData({});
                toast.dismiss();
                toast.error(response.message, {
                    type: "error",
                    className: "e-toast",
                });
            }
            setLoading(false);
            setClearFilter(false);
        });
    };

    // API - Download data
    const _download = () => {
        setDownloadLoader(true);

        let is_post = true;
        let url = "store/download";
        let data = {
            "page_num": currentPage,
            "limit": ITEMS_PER_PAGE,
            "search_query": searchValue,
            "country_id": selectedCountry?.value || null
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                const file_url = response.file_url;
                const link = document.createElement('a');
                link.href = file_url;
                link.download = '';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                toast.dismiss();
                toast.error(response.message, {
                    type: "error",
                    className: "e-toast",
                });
            }
            setDownloadLoader(false);
        });
    };

    // API - Upload store data (file URL from ManageProductModal presigned upload)
    const _uploadStore = (file_url) => {
        setModalBtnLoader(true);
        let is_post = true;
        let url = 'store/upload';
        let data = {
            "file": file_url
        };
        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setUploadResponse(true);
            } else {
                toast.dismiss();
                toast.error(response.message, {
                    type: "error",
                    className: "e-toast",
                });
            }
            setModalBtnLoader(false);
        });
    };

    return (
        <section className='pb-5'>
            <div className='e-bg-azureish-white padding-12px-top padding-98px-bottom padding-52px-lr'>
                <div className='d-flex align-items-center gap-2 justify-content-between'>
                    <h1 className='mb-0 e-primary-font-playfair e-font-24 line-height-32px e-text-rich-black e-font-weight-600'>
                        Manage Stores
                    </h1>
                    <div className='position-relative'>
                        <img src={require("../../../Assets/Image/upload.svg").default}
                            draggable={false}
                            height={24}
                            width={24}
                            className='position-absolute left-16px top-8px d-flex'
                            alt='upload' />
                        <PrimaryButton name="Upload Store data"
                            className="w-202px padding-14px-tb e-border-radius-16 px-3 text-end e-font-16 line-height-12px"
                            onPress={() => setUploadModal(true)} />
                    </div>
                </div>
            </div>

            <div className='margin-neg-66px-top padding-52px-lr'>
                <div className='e-bg-white e-admin-box-shadow py-4 e-border-radius-16 e-border-1px-platinum-dark'>
                    <div className='px-4'>
                        <div className='row'>
                            <div className='col-lg-4 col-md-5 col-sm-4'>
                                <h4 className='mb-0 e-text-rich-black e-alt-font-noto-sans e-font-20 e-font-weight-600 line-height-28px'>
                                    Stores
                                </h4>
                            </div>
                            <div className='col-lg-8 col-md-7 col-sm-8 pt-xl-0 pt-lg-0 pt-md-0 pt-sm-0 pt-2 text-end'>
                                <div className='d-flex justify-content-end align-items-center gap-3'>
                                    <DownloadButton loader={downloadLoader}
                                        disabled={!storeData?.data?.length}
                                        onClick={_download} />

                                    <div className='position-relative overflow-hidden'>
                                        <CustomSearchInput placeholder="Search by name, ID or store"
                                            type="text"
                                            id="search"
                                            require
                                            search
                                            value={searchValue}
                                            className="w-400px padding-10px-tb"
                                            labelStyle="e-font-weight-500"
                                            handleChange={(e) => {
                                                setSearchValue(e);
                                                if (e === "") {
                                                    setSortField(null);
                                                    setSortDirection(null);
                                                    setCurrentPage(1);
                                                    _getStoreList(1, "", null, null);
                                                }
                                            }}
                                            onKeyPress={() => {
                                                setCurrentPage(1);
                                                _getStoreList(1, searchValue);
                                            }}
                                            handleClear={() => {
                                                setSearchValue("");
                                                setSortField(null);
                                                setSortDirection(null);
                                                setCurrentPage(1);
                                                _getStoreList(1, "", null, null);
                                            }} />

                                        {searchValue.length !== 0 &&
                                            <span className='position-absolute h-100 top-0 px-3 d-flex align-items-center justify-content-center e-bg-indigo end-0 cursor-pointer transform-180deg e-border-radius-8px-0-0-8px'
                                                onClick={() => {
                                                    setCurrentPage(1);
                                                    _getStoreList(1, searchValue);
                                                }}>
                                                <Icon icon="arrow-left"
                                                    size="24px"
                                                    color="#ffffff"
                                                    onPress={() => { }} />
                                            </span>
                                        }
                                    </div>

                                    <div>
                                        <img src={toggleFilter ? require("../../../Assets/Image/filter-icon-white.svg").default : require("../../../Assets/Image/filter-icon.svg").default}
                                            draggable={false}
                                            height={40}
                                            width={40}
                                            className={` ${toggleFilter ? `e-bg-indigo` : ``} transition e-border-1px-anti-flash-white e-border-radius-8 p-2 cursor-pointer`}
                                            alt='filter'
                                            onClick={() => setToggleFilter(!toggleFilter)} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* filter */}
                    {toggleFilter &&
                        <div className='e-bg-anti-flash-white-dark py-3 px-4 mt-3'>
                            <div className='row align-items-end'>
                                <div className='col-md-3 col-sm-4 col-6'>
                                    <CustomSelectBox label="Country"
                                        value={selectedCountry}
                                        options={countryOptions}
                                        loader={filterLoader}
                                        type={2}
                                        className="e-bg-white e-border-radius-4 e-table-select"
                                        minHeight="34px"
                                        padding="0 8px"
                                        wrapperHeight="0px"
                                        labelClass="e-font-weight-600"
                                        optional={true}
                                        isSearchable={false}
                                        selectChange={(value) => setSelectedCountry(value)}
                                    />
                                </div>

                                <div className='col-md-9 col-sm-8 col-6 mt-auto'>
                                    <div className='d-flex gap-3 align-items-center justify-content-end'>
                                        <TextButton name="Clear all"
                                            onPress={_handleClearAll} />
                                        <PrimaryButton name="Filter now"
                                            className="px-3 py-1 e-border-radius-8"
                                            onPress={() => {
                                                setLoading(true);
                                                setCurrentPage(1);
                                                _getStoreList(1);
                                            }} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    }

                    {loading ?
                        <Loader />
                        :
                        <>
                            {Object.keys(storeData).length !== 0 && storeData?.data?.length !== 0 ?
                                <div className='e-country-admin-table cursor-default'>
                                    <DataTableComponent
                                        key={`sa-store-table-${currentPage}-${sortField || 'none'}-${sortDirection || 'none'}`}
                                        columns={TABLE_COLUMNS}
                                        pagination={true}
                                        data={storeData.data}
                                        paginationPerPage={ITEMS_PER_PAGE}
                                        paginationServer={true}
                                        paginationTotalRows={storeData.total_count}
                                        paginationDefaultPage={currentPage}
                                        paginationResetDefaultPage={false}
                                        sortServer={true}
                                        defaultSortFieldId={sortField}
                                        defaultSortAsc={sortDirection !== 'desc'}
                                        onSort={_handleSort}
                                        onChangePage={_handlePageChange} />
                                </div>
                                :
                                <EmptyScreen
                                    className="min-vh-100 d-flex flex-column align-items-center justify-content-center"
                                    message="No stores found" />
                            }
                        </>
                    }
                </div>
            </div>

            {uploadModal === true &&
                <ManageProductModal type={1}
                    title="Upload"
                    description="Select and upload the required file to proceed."
                    buttonLabel="Upload"
                    status={uploadResponse}
                    loader={modalBtnLoader}
                    handleClose={() => {
                        setTimeout(() => {
                            setUploadModal(false);
                            setUploadResponse(false);
                        }, 200);
                    }}
                    handleSuccess={() => {
                        setTimeout(() => {
                            setUploadModal(false);
                            setUploadResponse(false);
                            setLoading(true);
                            setCurrentPage(1);
                            _getStoreList(1);
                        }, 200);

                        toast.dismiss();
                        toast.success("Successfully uploaded store data", {
                            type: "success",
                            className: "e-toast",
                        });
                    }}
                    handleSubmit={_uploadStore}
                />
            }

            {editStore !== null &&
                <ManageStoreModal
                    editData={editStore}
                    handleClose={() => {
                        setTimeout(() => {
                            setEditStore(null);
                        }, 200);
                    }}
                    handleSuccess={() => {
                        setTimeout(() => {
                            setEditStore(null);
                            setLoading(true);
                            _getStoreList();
                        }, 200);
                    }}
                />
            }
        </section>
    )
}

export default StoreListing;
